/**
 * Global-surgical-period-aware billing (`BACKLOG.md` M7) — orthopedics' own CPT-to-global-period-days
 * catalog. The window-arithmetic/modifier-selection *mechanism* moved to `../globalSurgicalPeriod.ts`
 * once ophthalmology (`BACKLOG.md` M10) became a second real consumer of it — this file re-exports
 * that mechanism for backward compatibility with every existing import site
 * (`orthopedicsRequests.ts`, `orthopedics/routes.ts`, `orthopedics/globalSurgicalPeriodService.ts`),
 * none of which needed to change.
 *
 * **The catalog below is UNREVIEWED data** — a small curated demo catalog (matching `rcm.ts`'s own
 * "small curated catalog, not a licensed code database" framing for CPT), with global-period-day
 * values carried over from general orthopedic-billing knowledge rather than checked against the
 * current-year CMS Physician Fee Schedule Relative Value File, which is the actual authoritative
 * source and which changes periodically. Do not treat any value in this table as billing-correct
 * until independently confirmed against that source. One exception: `26055` was checked against the
 * CMS PFS Look-Up Tool on 2026-08-24 and corrected from an originally-miscoded 10-day to the real
 * 90-day global period — see that entry's own inline comment. The other seven codes in the catalog
 * were checked at the same time and found already correct; only `26055` needed a fix.
 */

export {
  computeGlobalPeriodWindow,
  determineGlobalPeriodModifier,
  GLOBAL_PERIOD_DAYS_VALUES,
  GLOBAL_PERIOD_MODIFIERS,
  GLOBAL_PERIOD_RELATIONSHIPS,
  globalPeriodRelationshipLabel,
  isGlobalPeriodDays,
  isGlobalPeriodRelationship,
  isWithinGlobalPeriod,
  type GlobalPeriodDays,
  type GlobalPeriodModifier,
  type GlobalPeriodModifierResult,
  type GlobalPeriodRelationship,
  type GlobalPeriodWindow,
} from '../globalSurgicalPeriod.ts';

import type { GlobalPeriodDays } from '../globalSurgicalPeriod.ts';

// --- Demo CPT-to-global-period catalog -----------------------------------------------------------

export interface OrthoGlobalPeriodCptCode {
  readonly code: string;
  readonly description: string;
  readonly globalPeriodDays: GlobalPeriodDays;
}

/** UNREVIEWED — see this module's header (all values except `26055`, corrected and cited inline
 * below). Ordered roughly by how often this demo's ortho patients would have them: joint
 * replacement, soft-tissue reconstruction, fracture care, trigger-finger release, then the one
 * genuinely lower-acuity procedure with no global period at all. */
export const ORTHO_GLOBAL_PERIOD_CPT_CODES: readonly OrthoGlobalPeriodCptCode[] = [
  { code: '27447', description: 'Total knee arthroplasty', globalPeriodDays: 90 },
  { code: '27130', description: 'Total hip arthroplasty', globalPeriodDays: 90 },
  {
    code: '29827',
    description: 'Arthroscopic rotator cuff repair',
    globalPeriodDays: 90,
  },
  {
    code: '29888',
    description: 'Arthroscopic ACL reconstruction',
    globalPeriodDays: 90,
  },
  { code: '64721', description: 'Open carpal tunnel release', globalPeriodDays: 90 },
  {
    code: '25605',
    description: 'Closed treatment of distal radius fracture, with manipulation',
    globalPeriodDays: 90,
  },
  {
    code: '26055',
    description: 'Tendon sheath incision (trigger finger release)',
    // Corrected 2026-08-24, confirmed against the CMS PFS Look-Up Tool: this is a 90-day global
    // procedure, not 10-day. The prior 10-day value was miscoded — with determineGlobalPeriodModifier
    // deriving "outside global period" purely off this table, a patient seen on postop day 11-90
    // would have been treated as ordinary separately-payable care instead of bundled/modifier-
    // required, the exact silent-misbilling shape this module's own header names as the highest risk.
    globalPeriodDays: 90,
  },
  {
    code: '20610',
    description: 'Arthrocentesis/injection, major joint',
    globalPeriodDays: 0,
  },
] as const;

export const ORTHO_GLOBAL_PERIOD_CPT_CODE_VALUES = ORTHO_GLOBAL_PERIOD_CPT_CODES.map(
  (c) => c.code,
) as [string, ...string[]];

export function findOrthoGlobalPeriodCptCode(code: string): OrthoGlobalPeriodCptCode | undefined {
  return ORTHO_GLOBAL_PERIOD_CPT_CODES.find((entry) => entry.code === code);
}
